if (!customElements.get('live-atomic-proof-video')) {
  customElements.define('live-atomic-proof-video', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.video = this.querySelector('[data-proof-media]');
      this.playButton = this.querySelector('[data-proof-play]');
      this.toggleButton = this.querySelector('[data-proof-toggle]');
      this.muteButton = this.querySelector('[data-proof-mute]');
      this.progress = this.querySelector('[data-proof-progress]');
      this.duration = this.querySelector('[data-proof-duration]');
      if (!this.video) return;

      // JS owns the controls from here; the native bar stays available only as the no-script fallback.
      this.video.removeAttribute('controls');
      this.classList.add('is-ready');

      this.playButton?.addEventListener('click', () => this.play());
      this.toggleButton?.addEventListener('click', () => this.toggle());
      this.muteButton?.addEventListener('click', () => this.setMuted(!this.video.muted));
      this.video.addEventListener('click', () => {
        if (this.classList.contains('is-playing') || this.classList.contains('is-paused')) this.toggle();
      });

      this.video.addEventListener('play', () => this.setState('is-playing'));
      this.video.addEventListener('pause', () => {
        if (!this.video.ended) this.setState('is-paused');
      });
      this.video.addEventListener('ended', () => this.reset());
      this.video.addEventListener('timeupdate', () => this.updateProgress());
      this.video.addEventListener('volumechange', () => this.classList.toggle('is-muted', this.video.muted));
      this.video.addEventListener('loadedmetadata', () => this.showDuration());
      if (this.video.readyState >= 1) this.showDuration();

      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting && !this.video.paused) this.video.pause();
          });
        }, { threshold: 0.2 }).observe(this);
      }
    }

    play() {
      this.video.muted = false;
      const attempt = this.video.play();
      if (attempt?.catch) {
        attempt.catch(() => {
          // Autoplay policy refused sound; fall back to the native bar so the visitor can still start it.
          this.video.setAttribute('controls', '');
          this.reset();
        });
      }
    }

    toggle() {
      if (this.video.paused) this.video.play().catch(() => {});
      else this.video.pause();
    }

    setMuted(muted) {
      this.video.muted = muted;
      if (this.muteButton) {
        this.muteButton.setAttribute('aria-pressed', String(muted));
        this.muteButton.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
      }
    }

    setState(state) {
      this.classList.remove('is-playing', 'is-paused');
      this.classList.add(state);
      if (this.toggleButton) this.toggleButton.setAttribute('aria-label', state === 'is-playing' ? 'Pause' : 'Play');
      if (state === 'is-playing') this.toggleButton?.focus({ preventScroll: true });
    }

    reset() {
      this.classList.remove('is-playing', 'is-paused');
      this.video.pause();
      this.video.currentTime = 0;
      this.video.load();
      if (this.progress) this.progress.style.width = '0%';
      this.playButton?.focus({ preventScroll: true });
    }

    updateProgress() {
      if (!this.progress || !this.video.duration) return;
      this.progress.style.width = `${(this.video.currentTime / this.video.duration) * 100}%`;
    }

    showDuration() {
      if (!this.duration || !Number.isFinite(this.video.duration)) return;
      const total = Math.round(this.video.duration);
      const minutes = Math.floor(total / 60);
      const seconds = String(total % 60).padStart(2, '0');
      this.duration.textContent = `${minutes}:${seconds}`;
      this.duration.hidden = false;
    }
  });
}
